<template>
    <section class="forum-thread-summary">
        <header>
            <ul class="info-blocks">
                <li>
                    <div class="info-label info-label-left">
                        Created
                    </div>
                    <div
                        class="info-value"
                        :title="formatFullDate(firstDate)">
                        {{ formatShortDate(firstDate) }}
                    </div>
                </li>
                <li>
                    <button @click="$emit('go-last-post')">
                        <div class="info-label info-label-left">
                            Last reply
                        </div>
                        <div
                            class="info-value"
                            :title="formatFullDate(lastDate)">
                            {{ formatShortDate(lastDate) }}
                        </div>
                    </button>
                </li>
                <li>
                    <div class="info-value">
                        {{ replyCount }}
                    </div>
                    <div class="info-label">
                        Replies
                    </div>
                </li>
                <li>
                    <div class="info-value">
                        {{ viewCount }}
                    </div>
                    <div class="info-label">
                        Views
                    </div>
                </li>
                <li>
                    <div class="info-value">
                        {{ userCount }}
                    </div>
                    <div class="info-label">
                        Users
                    </div>
                </li>
                <li>
                    <div class="info-value">
                        {{ likeCount }}
                    </div>
                    <div class="info-label">
                        Likes
                    </div>
                </li>
            </ul>

            <button
                type="button"
                class="body-toggle"
                title="Expand"
                @click="isOpen = !isOpen"
                v-if="showExpand">

                <i
                    class="fa fa-chevron-up"
                    v-if="isOpen">
                </i>
                <i
                    class="fa fa-chevron-down"
                    v-else>
                </i>
            </button>
        </header>

        <div
            class="body"
            v-show="isOpen">

            <div
                class="filters body-section"
                v-show="showFilters">

                <div
                    class="terms"
                    v-show="isEditing || termTypes.length > 0">
                    <div class="info-label">
                        Term Types
                    </div>
                    <multiselect
                        :options="termTypeOptions"
                        :value="termTypes"
                        @input="$emit('update:termTypes', $event)"
                        v-if="isEditing">
                    </multiselect>
                    <ul v-else>
                        <li v-for="tt in sortedTermTypes">
                            {{ formatTermType(tt) }}
                        </li>
                    </ul>
                </div>

                <div
                    class="paths"
                    v-show="showPaths">
                    <div class="info-label">
                        Paths
                    </div>
                    <multiselect
                        :options="pathOptions"
                        :value="pathIds"
                        @input="$emit('update:pathIds', $event)"
                        v-if="isEditing">
                    </multiselect>
                    <ul v-else>
                        <li v-for="id in pathIds">
                            {{ formatPathId(id) }}
                        </li>
                    </ul>
                </div>

                <div
                    class="assignments"
                    v-if="isEditing || assignmentIds.length > 0">
                    <div class="info-label">
                        Assignments
                    </div>
                    <div v-if="isEditing">
                        <ul>
                            <li v-for="asnId in assignmentIds">
                                <div class="flex">
                                    <div class="flex-full">
                                        {{ formatAssignmentId(asnId) }}
                                    </div>
                                    <button
                                        type="button"
                                        @click="deselectAssignment(asnId)">
                                        <i class="fa fa-close"></i>
                                    </button>
                                </div>
                            </li>
                        </ul>
                        <search-select
                            :options="assignmentOptions"
                            :multiple="true"
                            :value="assignmentIds"
                            @input="$emit('update:assignmentIds', $event)">
                        </search-select>
                    </div>
                    <ul v-else>
                        <li v-for="id in assignmentIds">
                            {{ formatAssignmentId(id) }}
                        </li>
                    </ul>
                </div>
            </div>

            <div
                class="tags body-section"
                v-show="isEditing || tags.length > 0">

                <div class="info-label">
                    Tags
                </div>
                <tag-select
                    :search="searchTags"
                    :canCreate="true"
                    v-model="tags"
                    v-if="isEditing">
                </tag-select>
                <tag-list
                    :value="tags"
                    v-else>
                </tag-list>
            </div>

            <div
                class="action-btns body-section"
                v-if="isEditing && canSave">

                <button
                    type="button"
                    class="btn btn-success"
                    @click="$emit('save')">
                    <i class="fa fa-save"></i>
                    Save
                </button>
                <button
                    type="button"
                    class="btn btn-default"
                    @click="$emit('cancel')">
                    Cancel
                </button>
            </div>

            <div
                class="action-btns body-section"
                v-if="!isEditing && canEdit">

                <button
                    type="button"
                    class="btn btn-default"
                    @click="$emit('edit')">
                    <i class="fa fa-pencil"></i>
                    Edit
                </button>
            </div>
        </div>
    </section>
</template>

<script>
    // ForumThreadSummary: a summary of the thread containing the number of
    // replies, the dates of activity, etc. Also provides controls to edit
    // thread associations, such as tags, term types, etc.

    // Events:
    //  - go-last-post (user clicked "last reply" date)
    //  - edit         (user clicked "edit")
    //  - cancel       (user cancelled their edits)
    //  - save         (user committed their edits)
    //  - update:termTypes
    //  - update:pathIds
    //  - update:assignmentIds
    //  - update:tagnames

    import AssignmentService from 'src/services/assignments';
    import ForumService      from 'src/services/forum';
    import PathService       from 'src/services/paths';
    import TermService       from 'src/services/terms';
    import ForumUtils        from 'src/utils/forum';
    import StringUtils       from 'src/utils/strings';
    import TermUtils         from 'src/utils/terms';
    import TagList           from '../tag-list';
    import Multiselect       from './multiselect';
    import SearchSelect      from './search-select';
    import TagSelect         from './tag-select';

    export default {
        components: {
            TagList,
            Multiselect,
            SearchSelect,
            TagSelect,
        },

        props: {
            thread: {
                type: Object, // ForumThread (see forum service)
                default: null,
            },

            termTypes: {
                type: Array, // string[]
                default: () => [],
            },

            pathIds: {
                type: Array, // number[]
                default: () => [],
            },

            assignmentIds: {
                type: Array, // number[]
                default: () => [],
            },

            tagNames: {
                type: Array, // string[]
                default: () => [],
            },

            // Show the edit button
            canEdit: {
                type: Boolean,
                default: false,
            },

            // Show the save/cancel buttons
            canSave: {
                type: Boolean,
                default: true,
            },

            // Show controls to modify the thread fields, like term types, etc.
            isEditing: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                isOpen: false,
                isLoading: true,

                tags: [],

                // TODO: These should be put in a Vuex store to avoid dup ajax
                // requests.
                termTypeOptions: [],
                pathOptions: [],
                assignmentOptions: [],
            };
        },

        computed: {
            firstDate() {
                return this.thread &&
                       this.thread.firstPost &&
                       this.thread.firstPost.createdAt;
            },

            lastDate() {
                return this.thread &&
                       this.thread.lastPost &&
                       this.thread.lastPost.createdAt;
            },

            replyCount() {
                return this.thread ? this.thread.postCount - 1 : 0;
            },

            viewCount() {
                return this.thread ? this.thread.viewCount : 0;
            },

            userCount() {
                return this.thread ? this.thread.userCount : 0;
            },

            likeCount() {
                return this.thread ? this.thread.likeCount : 0;
            },

            sortedTermTypes() {
                return TermUtils.sortTypes(this.termTypes);
            },

            showExpand() {
                // We only want to show the expand button if the body has
                // something, like tags or an edit button.
                return (
                    this.canEdit ||
                    this.showFilters ||
                    this.tags.length > 0
                );
            },

            showFilters() {
                if (this.isEditing) {
                    return true;
                } else {
                    return (
                        this.termTypes.length > 0 ||
                        this.pathIds.length > 0 ||
                        this.assignmentIds.length > 0
                    );
                }
            },

            showPaths() {
                // Only show paths if a non-foundation term is selected.
                if (!this.termTypes.find(tt => tt !== 'foundation')) {
                    return false;
                }
                // if (localStorage.path && localStorage.path == '9200') {
                //     // houdini path
                //     return false;
                // }
                if (this.isEditing) {
                    return true;
                } else {
                    // If not editing, then only show paths if there are some.
                    return this.pathIds.length > 0;
                }
            },
        },

        watch: {
            tagNames: {
                handler() {
                    // We need to do a deep compare here to avoid loops with our
                    // syncing.
                    const oldTagNames = this.tags.map(t => t.name);
                    const str1 = JSON.stringify(oldTagNames);
                    const str2 = JSON.stringify(this.tagNames);
                    if (str1 !== str2) {
                        this.tags = this.tagNames.map(tn => ({ name: tn }));
                    }
                },
                immediate: true,
            },

            tags() {
                const newTagNames = this.tags.map(t => t.name);
                this.$emit('update:tagNames', newTagNames);
            },
        },

        async created() {
            this.loading = true;
            await this.fetchOptions();
            this.loading = false;
        },

        methods: {
            async fetchOptions() {

                // Start fetching the data
                const termTypesProm = TermService.getTermTypes();
                const pathsProm = PathService.getPaths();
                const assignmentsProm = AssignmentService.get().then(res => {
                    if (res.data.success) {
                        return res.data.assignments;
                    } else {
                        throw new Error(res.data.error);
                    }
                });

                // Wait for the data and transform into options
                const termTypes = TermUtils.sortTypes(await termTypesProm);
                this.termTypeOptions = termTypes.map(type => {
                    return {
                        label: StringUtils.toTitleCase(type),
                        value: type,
                        term: null,
                    };
                });
                this.pathOptions = (await pathsProm).map(path => {
                    return {
                        label: path.name,
                        value: path.id,
                        path,
                    };
                }).filter(p => p.value != 9200);

                this.assignmentOptions = (await assignmentsProm).map(asn => {
                    return {
                        label: asn.name,
                        value: asn.id,
                        assignment: asn,
                    };
                });
            },

            async searchTags(query) {
                return await ForumService.searchTags(query);
            },

            open() {
                this.isOpen = true;
            },

            close() {
                this.isOpen = true;
            },

            deselectAssignment(asnId) {
                const newIds = this.assignmentIds.filter(id => id !== asnId);
                this.$emit('update:assignmentIds', newIds);
            },

            formatFullDate(date) {
                if (date) {
                    return ForumUtils.formatFullDate(date);
                } else {
                    return 'No date';
                }
            },

            formatShortDate(date) {
                if (date) {
                    return ForumUtils.formatRecentDate(date);
                } else {
                    return 'N/A';
                }
            },

            formatTermType(termType) {
                return StringUtils.toTitleCase(termType);
            },

            formatPathId(pathId) {
                const o = this.pathOptions.find(o => o.value === pathId);
                return o && o.label;
            },

            formatAssignmentId(asnId) {
                const o = this.assignmentOptions.find(o => o.value === asnId);
                return o && o.label;
            },
        },
    }
</script>

<style lang="scss" scoped>
    .flex {
        display: flex;
    }
    .flex-full {
        flex: 1;
    }

    .forum-thread-summary {
        background: #333;
        border: 1px solid #444;
    }

    header {
        display: flex;

        button {
            background: none;
            border: none;
            display: block;
            padding: 0;
        }
    }

    .info-blocks {
        list-style: none;
        margin: 0;
        padding: 0;
        flex: 1;
        display: flex;
        overflow: hidden;

        li {
            padding: 8px 11px;
        }

        .info-value {
            color: #ccc;
            font-size: 18px;
            text-align: center;
        }

        .info-label {
            color: #999;
            font-size: 12px;
            text-align: center;
        }

        .info-label-left {
            text-align: left;
        }
    }

    .body-toggle {
        width: 58px;
        font-size: 18px;
        border-left: 1px solid #444;
    }

    .body {
        background: #2d2d2d;
        border-top: 1px solid #444;

        .info-label {
            color: #999;
            font-size: 12px;
            margin: 8px 0 10px;
        }

        ul:not(.multiselect):not(.tag-list) {
            padding-left: 20px;
            list-style: disc;
        }

        .forum-multiselect {
            margin: 0;
        }

        .forum-search-select {
            z-index: 1;
        }

        .body-section {
            border-bottom: 1px solid #3d3d3d;

            &:last-child {
                border-bottom: none;
            }
        }

        .filters {
            display: flex;
            padding-bottom: 11px;
            position: relative;

            .terms,
            .paths,
            .assignments {
                flex: 1;
                margin-left: 11px;

                &:last-child {
                    margin-right: 11px;
                }
            }

            .terms {
                // When paths is hidden, we want terms to use 1/3 of the width
                // and assignments to use the other 2/3. This works better for
                // all the long assignment names, plus then the term buttons
                // don't shift around when the user clicks a non-foundation term
                // and unveils the path controls.
                max-width: calc(33% - 11px);
            }

            .assignments {
                li button {
                    background: none;
                    border: none;
                    display: block;
                    padding: 0;
                    width: 20px;
                    height: 20px;

                    &:hover {
                        background: #444;
                    }

                    i {
                        position: relative;
                        top: -1px;
                    }
                }
            }
        }

        .tags {
            padding: 0 11px 1px;
            position: relative;

            .tag-select {
                // The tag list has a bottom margin of 10px, so we need to
                // match.
                margin-bottom: 10px;
            }
        }

        .action-btns {
            text-align: right;
            padding: 11px;

            .btn {
                padding: 4px 10px;

                i {
                    margin-right: 3px;
                }
            }
        }
    }
</style>

<style lang="scss">
    .forum-thread-summary {
        .multiselect li {
            display: block;
            margin: 10px 0 0;
        }
    }
</style>



// WEBPACK FOOTER //
// thread-summary.vue?38e8bcd1